import React from "react";
import { useTranslation } from "react-i18next";
import {
  formatTimeEntryText,
  timeEntryTo24h,
  type TimeEntry,
} from "./timeEntry";

interface Props {
  id: string;
  value: TimeEntry;
  onChange: (value: TimeEntry) => void;
}

/** Single manually typed time (hh:mm) with an AM/PM toggle. */
export const TimeEntryField: React.FC<Props> = ({ id, value, onChange }) => {
  const { t } = useTranslation("patients");
  const isInvalid = value.text.trim() !== "" && timeEntryTo24h(value) === null;

  return (
    <div>
      <label htmlFor={id} className="text-sm font-medium mb-1 block">
        {t("scheduling.time")} <span className="text-destructive-text">*</span>
      </label>
      <div className="flex gap-2">
        <input
          id={id}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          placeholder={t("scheduling.timePlaceholder")}
          maxLength={5}
          value={value.text}
          onChange={(e) =>
            onChange({ ...value, text: e.target.value.replace(/[^\d:]/g, "") })
          }
          onBlur={() => onChange({ ...value, text: formatTimeEntryText(value.text) })}
          aria-invalid={isInvalid}
          className={`w-28 border rounded-md px-3 py-2 text-sm ${
            isInvalid ? "border-destructive" : ""
          }`}
        />
        <div className="flex border rounded-md overflow-hidden">
          {(["am", "pm"] as const).map((period) => (
            <button
              key={period}
              type="button"
              onClick={() => onChange({ ...value, period })}
              aria-pressed={value.period === period}
              className={`px-4 py-2 text-sm cursor-pointer uppercase ${
                value.period === period ? "bg-primary text-white" : "bg-gray-100"
              }`}
            >
              {period}
            </button>
          ))}
        </div>
      </div>
      {isInvalid && (
        <p className="mt-1 text-xs text-destructive-text">
          {t("scheduling.invalidTimeError")}
        </p>
      )}
    </div>
  );
};
